/**
 * WA-ONBOARD-001 — the client half of the WhatsApp activation opt-in.
 *
 * Atlas already notifies a human of every new signup with a `wa.me` link, and that human writes to
 * the merchant by hand. Handing that to an automated agent turns it into a business-initiated
 * WhatsApp message, which Meta permits only with prior opt-in — so the funnel now asks, on the
 * screen where the number itself is entered, and this is what it sends.
 *
 * ⛔ THE ONE RULE: only a literal `true` is consent.
 *
 * Not a truthy value, not a filled-in phone field, not "they got to the end of signup". A default,
 * a `||`, or a `Boolean(...)` over a loosely-typed value in this file is the difference between an
 * opt-in and a policy violation, and none of them looks wrong in a diff.
 */

/** The funnel field id. Also the key `FunnelStructure` registers the checkbox under. */
export const WHATSAPP_OPT_IN_FIELD = 'whatsappOptIn'

/**
 * Where the funnel stores WHEN the box was ticked.
 *
 * Separate from the boolean because react-hook-form gives a field one value, and the instant has
 * to be captured at the moment of the act — the phone screen — not at signup submit, which is
 * three screens later (email, password). The DTO, the ledger column and the migration all describe
 * this as "the instant the box was ticked"; taking it at submit would make all three untrue.
 */
export const WHATSAPP_OPT_IN_AT_FIELD = 'whatsappOptInAt'

/**
 * Build the `whatsappOnboarding` block of the organization-creation payload.
 *
 * ⚠️ ALWAYS RETURNS A BLOCK, including for a decline. The backend distinguishes three states, and
 * the distinction is the whole reason the ledger is trustworthy:
 *
 *     block absent          → this app never asked (every bundle older than this feature)
 *     block, optIn: false   → the merchant was shown the box and left it unticked
 *     block, optIn: true    → the merchant ticked it
 *
 * Omitting the block for a decline would collapse the first two, and then a future "message the
 * organizations we have no consent record for" campaign would message everyone who said no.
 */
export const buildWhatsappOnboardingConsent = (funnelAnswers, { locale, now } = {}) => {
  const optIn = funnelAnswers?.[WHATSAPP_OPT_IN_FIELD] === true
  const consent = {
    optIn,
    locale: locale || null,
    phone: funnelAnswers?.phone,
  }
  // A consent instant only exists where there was a consent. Stamping one on a decline would put
  // a date on a refusal, which reads — to anyone auditing later — like the opposite of what
  // happened.
  if (optIn) {
    // The checkbox's own instant is the truthful one. The submit instant is the FALLBACK, for a
    // `funnelAnswers` persisted by an older build (the atom is AsyncStorage-backed, so a
    // half-finished funnel survives an app update) — slightly late beats absent, but it is never
    // the normal path.
    const ticked = funnelAnswers?.[WHATSAPP_OPT_IN_AT_FIELD]
    const parsed = typeof ticked === 'string' ? Date.parse(ticked) : NaN
    consent.optInAt = Number.isNaN(parsed)
      ? (now instanceof Date ? now : new Date()).toISOString()
      : new Date(parsed).toISOString()
  }
  return consent
}
